import { useEffect, useState } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Table } from '@/components/ui/Table';
import type { TradingDeploymentInputsDto, TradingInputDto, TradingInputEntryDto, TradingInputKind } from '@/api/trading';
import { formatTime } from './TradingShared';
import styles from '@/pages/TradingPage.module.css';

export interface DraftRow {
  key: string; kind: TradingInputKind; symbol: string; text: string;
}

const directionOptions = ['LONG', 'OFF', 'SHORT'] as const;
const rowId = (key: string, symbol: string) => JSON.stringify([key, symbol]);

export function decodeInputValue(valueJson: string | null): string | null {
  if (valueJson == null) return null;
  try {
    const parsed = JSON.parse(valueJson) as unknown;
    if (typeof parsed === 'number' && Number.isFinite(parsed)) return String(parsed);
    if (typeof parsed === 'boolean') return parsed ? 'true' : 'false';
    if (typeof parsed === 'string') return parsed;
  } catch {
    // Fall back to the raw text when the stored JSON is unreadable.
  }
  return valueJson;
}

export function normalizeDirectionValue(text: string) {
  const upper = text.trim().toUpperCase();
  if (upper === 'LONG' || upper === 'BULLISH') return 'LONG';
  if (upper === 'SHORT' || upper === 'BEARISH') return 'SHORT';
  return 'OFF';
}

function encodeInputValue(kind: TradingInputKind, text: string) {
  if (kind === 'Number') return JSON.stringify(Number(text));
  if (kind === 'Boolean') return JSON.stringify(text === 'true');
  if (kind === 'Direction') return JSON.stringify(normalizeDirectionValue(text));
  return JSON.stringify(text);
}

export function isFiniteDraftNumber(row: DraftRow) {
  return row.kind !== 'Number' || (row.text.trim() !== '' && Number.isFinite(Number(row.text)));
}

export function seedDraft(view: TradingDeploymentInputsDto): Record<string, DraftRow> {
  const draft: Record<string, DraftRow> = {};
  for (const input of view.inputs) {
    for (const override of input.overrides) {
      const symbol = override.symbol ?? '';
      draft[rowId(input.key, symbol)] = { key: input.key, kind: input.kind, symbol, text: decodeInputValue(override.valueJson) ?? '' };
    }
  }
  return draft;
}

export function toSaveEntries(rows: readonly DraftRow[], inputs: readonly TradingInputDto[]): TradingInputEntryDto[] {
  const declaredByKey = new Map(inputs.map((input) => [input.key, input]));
  const entries: TradingInputEntryDto[] = [];
  for (const row of rows) {
    const declared = declaredByKey.get(row.key);
    if (!declared) continue;
    const valueJson = encodeInputValue(row.kind, row.text);
    if (valueJson === declared.defaultValueJson) continue;
    entries.push({ key: row.key, kind: row.kind, valueJson, symbol: row.symbol || null });
  }
  return entries;
}

interface OperatorInputsSectionProps {
  view: TradingDeploymentInputsDto | undefined;
  loading?: boolean;
  saving: boolean;
  error?: string | null;
  onSave: (entries: TradingInputEntryDto[]) => void;
}

export function OperatorInputsSection({ view, loading = false, saving, error = null, onSave }: OperatorInputsSectionProps) {
  const inputs = view?.inputs ?? [];
  const symbols = view?.symbols ?? [];
  const matrix = symbols.length > 1;
  const [draft, setDraft] = useState<Record<string, DraftRow>>(() => (view ? seedDraft(view) : {}));
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!dirty && view) setDraft(seedDraft(view));
  }, [view, dirty]);

  function edit(mutate: (next: Record<string, DraftRow>) => void) {
    setDirty(true);
    setDraft((current) => { const next = { ...current }; mutate(next); return next; });
  }

  function setRow(key: string, kind: TradingInputKind, symbol: string, text: string) {
    edit((next) => { next[rowId(key, symbol)] = { key, kind, symbol, text }; });
  }

  function clearRow(key: string, symbol: string) {
    edit((next) => { delete next[rowId(key, symbol)]; });
  }

  function allOff() {
    edit((next) => {
      for (const input of inputs) {
        if (input.kind === 'Direction') next[rowId(input.key, '')] = { key: input.key, kind: 'Direction', symbol: '', text: 'OFF' };
      }
    });
  }

  function handleSave() {
    onSave(toSaveEntries(Object.values(draft), inputs));
  }

  function draftRow(key: string, symbol: string) { return draft[rowId(key, symbol)]; }

  function overrideNote(input: TradingInputDto, symbol: string) {
    const override = input.overrides.find((item) => (item.symbol ?? '') === symbol);
    if (!override) return null;
    return <small className={styles.block}>by {override.updatedByUserId ?? 'operator'} · {formatTime(override.updatedUtc)}</small>;
  }

  function effectiveText(input: TradingInputDto, symbol: string) {
    const own = draftRow(input.key, symbol);
    if (own) return own.text;
    if (symbol !== '') {
      const wide = draftRow(input.key, '');
      if (wide) return wide.text;
    }
    return decodeInputValue(input.defaultValueJson) ?? '—';
  }

  function directionCell(input: TradingInputDto, symbol: string, inheritable: boolean) {
    const own = draftRow(input.key, symbol);
    const selected = normalizeDirectionValue(own?.text ?? effectiveText(input, symbol));
    const inherited = !own && symbol !== '';
    return <div>
      <div className={styles.actions}>
        {directionOptions.map((option) => <Button
          key={option}
          type="button"
          variant={selected === option ? 'primary' : 'ghost'}
          aria-pressed={selected === option}
          disabled={saving}
          onClick={() => setRow(input.key, 'Direction', symbol, option)}
        >{option}</Button>)}
        {inheritable && own && <Button type="button" variant="ghost" disabled={saving} onClick={() => clearRow(input.key, symbol)}>Inherit</Button>}
      </div>
      {inherited && <small className={styles.block}>inherits {selected.toLowerCase()}</small>}
      {overrideNote(input, symbol)}
    </div>;
  }

  function valueCell(input: TradingInputDto, symbol: string) {
    const own = draftRow(input.key, symbol);
    const fallback = decodeInputValue(input.defaultValueJson) ?? '';
    if (input.kind === 'Number') {
      return <div>
        <Input type="number" aria-label={`${input.label ?? input.key}${symbol ? ` (${symbol})` : ''}`} value={own?.text ?? fallback} disabled={saving} onChange={(event) => {
          const text = event.target.value;
          if (text.trim() === '') clearRow(input.key, symbol);
          else setRow(input.key, 'Number', symbol, text);
        }}/>
        {own && !isFiniteDraftNumber(own) && <span className={styles.error}>Enter a finite number.</span>}
        {overrideNote(input, symbol)}
      </div>;
    }
    if (input.kind === 'Boolean') {
      return <div>
        <Input type="checkbox" aria-label={`${input.label ?? input.key}${symbol ? ` (${symbol})` : ''}`} checked={(own?.text ?? fallback) === 'true'} disabled={saving} onChange={(event) => setRow(input.key, 'Boolean', symbol, event.target.checked ? 'true' : 'false')}/>
        {overrideNote(input, symbol)}
      </div>;
    }
    return <div>
      <Input type="text" aria-label={`${input.label ?? input.key}${symbol ? ` (${symbol})` : ''}`} value={own?.text ?? fallback} disabled={saving} onChange={(event) => setRow(input.key, 'Text', symbol, event.target.value)}/>
      {overrideNote(input, symbol)}
    </div>;
  }

  function cell(input: TradingInputDto, symbol: string, inheritable: boolean) {
    if (input.kind === 'Direction') return directionCell(input, symbol, inheritable);
    if (symbol !== '') {
      return <div>
        <small className={styles.block}>{effectiveText(input, symbol)}</small>
        {overrideNote(input, symbol)}
      </div>;
    }
    return valueCell(input, symbol);
  }

  const hasDirection = inputs.some((input) => input.kind === 'Direction');
  const invalidNumber = Object.values(draft).some((row) => !isFiniteDraftNumber(row));
  const headers = matrix ? ['Input', 'Kind', 'Deployment-wide', ...symbols] : ['Input', 'Kind', 'Value'];

  return <section className={styles.section} aria-labelledby="operator-inputs-heading">
    <div className={styles.sectionTitle}>
      <div><h2 id="operator-inputs-heading">Operator inputs</h2><p>Overrides replace declared defaults. Symbol rows take precedence over the deployment-wide row, which takes precedence over the default.</p></div>
      {!!inputs.length && <div className={styles.actions}>
        <Button type="button" variant="outline-warning" disabled={saving || !hasDirection} onClick={allOff}>ALL OFF</Button>
        <Button type="button" disabled={saving || invalidNumber} onClick={handleSave}>{saving ? 'Saving…' : 'Save inputs'}</Button>
      </div>}
    </div>
    {error && <Alert variant="danger">{error}</Alert>}
    {loading && <p className="statusText statusTextMuted">Loading operator inputs…</p>}
    {!loading && !inputs.length && <p className={styles.empty}>This strategy declares no operator inputs.</p>}
    {!!view && !!inputs.length && <>
      <small className={styles.block}>Max positions per direction: {view.maxPositionsPerDirection}</small>
      <div className={styles.tableWrap}><Table headers={headers}>
        {inputs.map((input) => <tr key={input.key}>
          <td><strong>{input.label ?? input.key}</strong><small className={styles.block}>{input.key}</small></td>
          <td><span className={styles.healthBadge}>{input.kind}</span></td>
          <td>{cell(input, '', false)}</td>
          {matrix && input.kind !== 'Direction' && <td colSpan={symbols.length} className={styles.empty}>Deployment-wide value applies to every symbol.</td>}
          {matrix && input.kind === 'Direction' && symbols.map((symbol) => <td key={symbol}>{cell(input, symbol, true)}</td>)}
        </tr>)}
      </Table></div>
    </>}
  </section>;
}
